import { useEffect, useState } from '../vendor/react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ColorPalette = 'forest' | 'ocean' | 'violet' | 'sunset';

export const colorPalettes: Array<{ id: ColorPalette; name: string; description: string; swatches: string[] }> = [
  { id: 'forest', name: 'Bosque', description: 'Verde sobrio y cálido', swatches: ['#174c42', '#e8a15b', '#dceae5'] },
  { id: 'ocean', name: 'Océano', description: 'Azul claro y profesional', swatches: ['#155e75', '#38bdf8', '#dff3f8'] },
  { id: 'violet', name: 'Violeta', description: 'Moderno y distintivo', swatches: ['#6d4bb5', '#a78bfa', '#eee9fb'] },
  { id: 'sunset', name: 'Coral', description: 'Cercano y enérgico', swatches: ['#a74235', '#f08a68', '#f8e5df'] },
];

const APPEARANCE_EVENT = 'nexo:appearance-changed';
const MODE_KEY = 'nexo-theme-mode';
const LEGACY_MODE_KEY = 'nexo-theme';
const PALETTE_KEY = 'nexo-color-palette';

function readMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(MODE_KEY) ?? window.localStorage.getItem(LEGACY_MODE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // La preferencia puede seguir activa durante la sesión aunque el navegador bloquee el almacenamiento.
  }
  return 'system';
}

function readPalette(): ColorPalette {
  try {
    const stored = window.localStorage.getItem(PALETTE_KEY);
    if (stored === 'forest' || stored === 'ocean' || stored === 'violet' || stored === 'sunset') return stored;
  } catch {
    // Se usa la paleta predeterminada cuando no hay almacenamiento disponible.
  }
  return 'forest';
}

function systemTheme(): 'light' | 'dark' {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function useTheme() {
  const [mode, setModeState] = useState<ThemeMode>(readMode);
  const [palette, setPaletteState] = useState<ColorPalette>(readPalette);
  const [systemValue, setSystemValue] = useState<'light' | 'dark'>(systemTheme);
  const resolvedTheme = mode === 'system' ? systemValue : mode;

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    const updateSystem = () => setSystemValue(media?.matches ? 'dark' : 'light');
    const syncAppearance = () => {
      setModeState(readMode());
      setPaletteState(readPalette());
    };
    media?.addEventListener?.('change', updateSystem);
    window.addEventListener(APPEARANCE_EVENT, syncAppearance);
    window.addEventListener('storage', syncAppearance);
    return () => {
      media?.removeEventListener?.('change', updateSystem);
      window.removeEventListener(APPEARANCE_EVENT, syncAppearance);
      window.removeEventListener('storage', syncAppearance);
    };
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.dataset.themeMode = mode;
    document.documentElement.dataset.palette = palette;
    document.documentElement.style.colorScheme = resolvedTheme;
  }, [mode, palette, resolvedTheme]);

  const persist = (nextMode: ThemeMode, nextPalette: ColorPalette) => {
    try {
      window.localStorage.setItem(MODE_KEY, nextMode);
      window.localStorage.setItem(LEGACY_MODE_KEY, nextMode === 'system' ? systemTheme() : nextMode);
      window.localStorage.setItem(PALETTE_KEY, nextPalette);
    } catch {
      // La apariencia sigue aplicada en la sesión actual.
    }
    window.dispatchEvent(new CustomEvent(APPEARANCE_EVENT));
  };

  const setMode = (next: ThemeMode) => {
    setModeState(next);
    persist(next, palette);
  };

  const setPalette = (next: ColorPalette) => {
    setPaletteState(next);
    persist(mode, next);
  };

  return {
    theme: resolvedTheme,
    mode,
    palette,
    setMode,
    setPalette,
    toggleTheme: () => setMode(resolvedTheme === 'light' ? 'dark' : 'light'),
    resetAppearance: () => {
      setModeState('system');
      setPaletteState('forest');
      persist('system', 'forest');
    },
  };
}
